import * as React from 'react'
import styled from 'styled-components'

import { t } from '@owl-nest/localize'

import { Option, SelectField, TextField, styles as S } from '@ulule/owl-kit-components/next'
import { BaseAddress } from '@owl-nest/api-client/latest'

const SpacedField = styled(TextField)`
  margin: 5px 10px;
`

const AddressField = styled(SpacedField)``

const HorizontalGroup = styled.div`
  margin: 10px -10px;
  display: flex;
  flex-direction: row;
`

const VerticalGroup = styled.div`
  margin: 10px -10px;
  display: flex;
  flex-direction: column;
`

const PostalCodeField = styled(SpacedField)`
  flex: 0 0 140px;
`
const CityField = styled(SpacedField)`
  flex: 1;
`
const StateField = styled(SpacedField)`
  flex: 1;
`

const CountrySelect = styled(SelectField)`
  display: flex;
  flex-direction: column;
  flex: 1;
  z-index: 1;
`

const CountryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 5px 10px;
  flex: 1;
`

type CountrySelectProps = {
  onChange: (value: string) => void
  selectedCountry: string
  label: string
  countryFilter?: string[]
  countries: { code: string; label: () => string }[]
  error?: React.ReactNode
  disabled: boolean
}

function CountryField({
  onChange,
  selectedCountry,
  label,
  countries,
  countryFilter,
  error,
  disabled,
}: CountrySelectProps): React.ReactElement<CountrySelectProps> {
  const filteredCountries = countries.filter((country) => {
    if (countryFilter && countryFilter.length >= 1) {
      return countryFilter.includes(country.code)
    } else {
      return true
    }
  })

  return (
    <CountryWrapper>
      <CountrySelect
        label={label}
        disabled={disabled}
        selected={filteredCountries.length === 1 ? [filteredCountries[0].code] : [selectedCountry]}
        height={128}
        onChange={(selected: { value: string; label: string }[]) => {
          onChange(selected[0].value)
        }}
        placeholder={t('Country')}
      >
        <CountryList countries={filteredCountries} />
      </CountrySelect>
      {error && <S.field.Message type="error">{error}</S.field.Message>}
    </CountryWrapper>
  )
}

type CountryListProps = {
  countries: { code: string; label: () => string }[]
}

const CountryList = React.memo(
  function CountryList({ countries }: CountryListProps) {
    return (
      <>
        {countries.map((country) => {
          return (
            <Option key={country.code} value={country.code} label={country.label()}>
              {country.label()}
            </Option>
          )
        })}
      </>
    )
  },
  () => true,
)

type AddressSubFormProps = {
  disabled?: boolean
  hideCountry?: boolean
  value: BaseAddress
  errors: { [K in keyof BaseAddress]?: string }
  touched: { [K in keyof BaseAddress]?: boolean }
  disableCountry?: boolean
  hideState?: boolean
  onChange: (address: BaseAddress) => void
  countries: { code: string; label: () => string }[]
  countryFilter?: string[]
}

export function AddressSubForm({
  hideCountry = false,
  disabled = false,
  value,
  onChange,
  touched,
  errors,
  countries,
  countryFilter,
  disableCountry = false,
  hideState = false,
}: AddressSubFormProps): React.ReactElement<AddressSubFormProps> {
  return (
    <>
      <VerticalGroup>
        <AddressField
          disabled={disabled}
          type="text"
          name="address1"
          label={t('Home address')}
          onChange={onFieldChange('address1')}
          value={value.address1}
          error={touched.address1 && errors.address1}
        />
        <AddressField
          disabled={disabled}
          type="text"
          name="address2"
          placeholder={t('Optional (business, floor, etc.)')}
          onChange={onFieldChange('address2')}
          value={value.address2}
          error={touched.address2 && errors.address2}
        />
      </VerticalGroup>
      <HorizontalGroup>
        <PostalCodeField
          disabled={disabled}
          type="text"
          name="postal_code"
          label={t('ZIP code')}
          onChange={onFieldChange('postal_code')}
          value={value.postal_code}
          error={touched.postal_code && errors.postal_code}
        />
        <CityField
          disabled={disabled}
          type="text"
          name="city"
          label={t('City')}
          onChange={onFieldChange('city')}
          value={value.city}
          error={touched.city && errors.city}
        />
      </HorizontalGroup>
      <HorizontalGroup>
        {!hideCountry && (
          <CountryField
            label={t('Country')}
            onChange={onFieldChange('country')}
            selectedCountry={value.country}
            countries={countries}
            countryFilter={countryFilter}
            disabled={disabled || disableCountry}
            error={touched.country && errors.country}
          />
        )}
        {!hideState && (
          <StateField
            disabled={disabled}
            type="text"
            name="state"
            label={t('State/Province')}
            placeholder={t('Optional')}
            onChange={onFieldChange('state')}
            value={value.state}
          />
        )}
      </HorizontalGroup>
    </>
  )

  function onFieldChange(name: keyof BaseAddress) {
    return (event: React.ChangeEvent<HTMLInputElement> | any): void => {
      if (typeof event !== 'string' && 'target' in event) {
        event = event.target.value
      }

      onChange({
        ...value,
        [name]: event,
      })
    }
  }
}
